import { Button, NumberInput, Popover, Radio, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { formatDuration, type ShiftPlanning } from '@reditelji/shared';
import { useMemo } from 'react';
import { Link } from 'react-router';
import type { TimelinePayload } from '../api/types';
import { plDod, plVrz } from '../lib/ui';
import { DAY_SCOPE, planDay, type PlanScope } from './derive';

export interface PlanCeilings {
  softDailyMin: number;
  maxDailyMin: number;
}

/** The settings a run uses: the stored rules with this session's ceilings. */
export function withCeilings(planning: ShiftPlanning, ceilings: PlanCeilings): ShiftPlanning {
  return { ...planning, ...ceilings };
}

export const scopeLabel = (scope: PlanScope, sectorName: string | null): string => {
  switch (scope.kind) {
    case 'day':
      return 'ves dan';
    case 'urgent':
      return 'samo nujna mesta';
    case 'sector':
      return `sektor ${sectorName ?? ''}`.trim();
  }
};

/**
 * What a run should cover, and how long a day it may hand out — decided
 * before it runs, with the numbers it would produce shown live. Answering
 * "not like that" here costs one look instead of a trip to Nastavitve.
 */
export function ScopePopover({
  data,
  planning,
  scope,
  ceilings,
  sectorFocus,
  opened,
  onOpenChange,
  onScope,
  onCeilings,
  onRun,
}: {
  data: TimelinePayload;
  planning: ShiftPlanning;
  scope: PlanScope;
  ceilings: PlanCeilings;
  sectorFocus: string | null;
  opened: boolean;
  onOpenChange: (open: boolean) => void;
  onScope: (scope: PlanScope) => void;
  onCeilings: (c: PlanCeilings) => void;
  onRun: () => void;
}) {
  const sector = data.sectors.find((s) => s.name === sectorFocus) ?? null;
  // One stable value to debounce: a fresh object each render would re-arm the
  // timer on every settle and never come to rest.
  const pending = useMemo(() => ({ scope, ceilings }), [scope, ceilings]);
  const [settled] = useDebouncedValue(pending, 150);

  const preview = useMemo(
    () => (opened ? planDay(data, withCeilings(planning, settled.ceilings), settled.scope) : null),
    [opened, data, planning, settled],
  );
  const people = preview ? new Set(preview.proposals.map((p) => p.volunteerId)).size : 0;
  const unfilledMin = preview?.unfilled.reduce((a, g) => a + (g.toMin - g.fromMin), 0) ?? 0;
  const badCeilings = ceilings.softDailyMin > ceilings.maxDailyMin;

  return (
    <Popover opened={opened} onChange={onOpenChange} position="top-end" width={320} withArrow shadow="md">
      <Popover.Target>
        <Button
          size="compact-sm"
          variant="default"
          title="obseg in dolžina dneva za ta predlog"
          onClick={() => onOpenChange(!opened)}
        >
          ▾
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={10}>
          <div>
            <div className="tl-detail-eyebrow" style={{ marginTop: 0 }}>
              KAJ NAJ ZAPOLNI
            </div>
            <Radio.Group
              value={scope.kind}
              onChange={(v) =>
                onScope(
                  v === 'urgent'
                    ? { kind: 'urgent' }
                    : v === 'sector' && sector
                      ? { kind: 'sector', sectorId: sector.id }
                      : DAY_SCOPE,
                )
              }
            >
              <Stack gap={5}>
                <Radio size="xs" value="day" label="Ves dan" />
                <Radio size="xs" value="urgent" label="Samo nujna mesta" />
                {sector ? <Radio size="xs" value="sector" label={`Sektor ${sector.name}`} /> : null}
              </Stack>
            </Radio.Group>
          </div>

          <div>
            <div className="tl-detail-eyebrow" style={{ marginTop: 0 }}>
              DAN PROSTOVOLJCA
            </div>
            <Stack gap={6}>
              <NumberInput
                size="xs"
                label="Priporočen dan"
                suffix=" min"
                step={30}
                min={60}
                max={1440}
                error={badCeilings}
                value={ceilings.softDailyMin}
                onChange={(v) => onCeilings({ ...ceilings, softDailyMin: Number(v) || 0 })}
              />
              <NumberInput
                size="xs"
                label="Največ na dan"
                suffix=" min"
                step={30}
                min={60}
                max={1440}
                value={ceilings.maxDailyMin}
                onChange={(v) => onCeilings({ ...ceilings, maxDailyMin: Number(v) || 0 })}
              />
            </Stack>
            <Text size="xs" c="dimmed" mt={4}>
              Velja za ta predlog.{' '}
              <Text component={Link} to="/nastavitve" size="xs" c="blue.7">
                Nastavitve
              </Text>{' '}
              jih shranijo za naprej.
            </Text>
          </div>

          <div className="tl-plan-card">
            {badCeilings ? (
              <Text size="xs" c="red.7">
                Priporočen dan ne sme biti daljši od največjega.
              </Text>
            ) : preview ? (
              <>
                <Text size="sm" fw={600}>
                  {plDod(preview.proposals.length)} · {people} ljudi
                </Text>
                <Text size="xs" c="dimmed">
                  {preview.unfilled.length > 0
                    ? `${plVrz(preview.unfilled.length)} (${formatDuration(unfilledMin)}) ostane brez kandidata.`
                    : 'Vse, kar je v obsegu, je pokrito.'}
                </Text>
              </>
            ) : null}
          </div>

          <Button
            size="compact-sm"
            style={{ background: 'oklch(0.35 0.05 245)' }}
            disabled={badCeilings || (preview?.proposals.length ?? 0) === 0}
            onClick={onRun}
          >
            Zaženi predlog
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
